import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { keyFromName } from "../tasks/taskData";
import { type LostReason, useLostReasons } from "./dealData";

// The whole team manages the lost reasons (D-036), like the task areas:
// add, rename, reorder, ask for a note, turn off. No delete: a reason in
// use stays, turned off.
export const LostReasonsDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { data: reasons = [] } = useLostReasons();
  const [newName, setNewName] = useState("");

  const run = async (action: () => Promise<unknown>) => {
    try {
      await action();
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
    await queryClient.invalidateQueries({ queryKey: ["lost_reasons"] });
  };

  const update = (reason: LostReason, data: Partial<LostReason>) =>
    dataProvider.update("lost_reasons", {
      id: reason.id,
      data,
      previousData: reason,
    });

  const rename = (reason: LostReason, name: string) => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === reason.name) return;
    return run(() => update(reason, { name: trimmed }));
  };

  const move = (index: number, offset: -1 | 1) => {
    const reason = reasons[index];
    const other = reasons[index + offset];
    if (!other) return;
    return run(async () => {
      await update(reason, { position: other.position });
      await update(other, { position: reason.position });
    });
  };

  const add = (event: React.FormEvent) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setNewName("");
    const position =
      Math.max(0, ...reasons.map((reason) => reason.position)) + 1;
    return run(() =>
      dataProvider.create("lost_reasons", {
        data: { key: keyFromName(name), name, position, active: true },
      }),
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{translate("tenax.deals.reasons.title")}</DialogTitle>
          <DialogDescription>
            {translate("tenax.deals.reasons.hint")}
          </DialogDescription>
        </DialogHeader>

        <ul className="flex flex-col gap-2">
          {reasons.map((reason, index) => (
            <li key={reason.id} className="flex flex-wrap items-center gap-1">
              <Input
                key={`${reason.id}-${reason.name}`}
                defaultValue={reason.name}
                aria-label={translate("tenax.deals.reasons.name")}
                className={`min-w-40 flex-1 ${
                  reason.active ? "" : "text-muted-foreground line-through"
                }`}
                onBlur={(event) => rename(reason, event.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={index === 0}
                onClick={() => move(index, -1)}
                aria-label={translate("tenax.deals.reasons.move_up")}
              >
                <ArrowUp />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={index === reasons.length - 1}
                onClick={() => move(index, 1)}
                aria-label={translate("tenax.deals.reasons.move_down")}
              >
                <ArrowDown />
              </Button>
              <label className="flex items-center gap-1 px-1 text-sm">
                <Checkbox
                  checked={reason.requires_note}
                  onCheckedChange={(checked) =>
                    run(() => update(reason, { requires_note: !!checked }))
                  }
                />
                {translate("tenax.deals.reasons.requires_note")}
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-24"
                onClick={() =>
                  run(() => update(reason, { active: !reason.active }))
                }
              >
                {translate(
                  reason.active
                    ? "tenax.deals.reasons.turn_off"
                    : "tenax.deals.reasons.turn_on",
                )}
              </Button>
            </li>
          ))}
        </ul>

        <form onSubmit={add} className="flex gap-2">
          <Input
            value={newName}
            placeholder={translate("tenax.deals.reasons.name")}
            aria-label={translate("tenax.deals.reasons.new")}
            onChange={(event) => setNewName(event.target.value)}
          />
          <Button type="submit" disabled={!newName.trim()}>
            {translate("tenax.deals.reasons.add")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
